import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ─── Paleta ───────────────────────────────────────────────────────────────────
const C = {
  laranja: '#D85A30',
  laranjaEscuro: '#B84A24',
  bg: '#f7f8fa',
  card: '#ffffff',
  borda: '#e4e9f0',
  texto: '#1a2233',
  textoSub: '#6b7a8d',
  verde: '#1db87a',
  vermelho: '#e74c3c',
  azul: '#2563eb',
  amarelo: '#f59e0b',
};

// ─── Utilitários ──────────────────────────────────────────────────────────────
const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const mesAtual = new Date().getMonth() + 1;
const anoAtual = new Date().getFullYear();

// Retorna o valor efetivo do contrato para um dado mês/ano.
// - c.valor                = valor ATUAL/ANTIGO (vigente até a data do reajuste)
// - c.valor_ultimo_reajuste = valor NOVO (entra a partir da data do reajuste)
// Antes da data do reajuste  → usa c.valor (antigo)
// Na data do reajuste ou depois → usa c.valor_ultimo_reajuste (novo)
function valorEfetivoContrato(c, mes, ano) {
  if (!c.data_ultimo_reajuste || !c.valor_ultimo_reajuste) return Number(c.valor || 0);
  const dataReajuste = new Date(c.data_ultimo_reajuste);
  const anoReajuste  = dataReajuste.getFullYear();
  const mesReajuste  = dataReajuste.getMonth() + 1;
  const consultadoAntes = ano < anoReajuste || (ano === anoReajuste && mes < mesReajuste);
  return consultadoAntes ? Number(c.valor || 0) : Number(c.valor_ultimo_reajuste);
}

const MESES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

// ─── Hook mobile ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = React.useState(window.innerWidth < 700);
  React.useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

// ─── Componentes internos ─────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: C.card, borderRadius: 14, border: `1px solid ${C.borda}`,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: 24, ...style
    }}>
      {children}
    </div>
  );
}

function SecaoTitulo({ cor = C.laranja, icone, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: cor + '18', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 16
      }}>{icone}</div>
      <span style={{ fontWeight: 700, fontSize: 15, color: C.texto }}>{label}</span>
    </div>
  );
}

function CampoValor({ label, value, onChange, cor }) {
  const [foco, setFoco] = React.useState(false);

  const formatarExibicao = (v) => {
    if (!v && v !== 0) return '';
    return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 13, color: cor || C.textoSub, fontWeight: 600, pointerEvents: 'none'
        }}>R$</span>
        <input
          type={foco ? 'number' : 'text'}
          value={foco ? value : formatarExibicao(value)}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFoco(true)}
          onBlur={() => setFoco(false)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const inputs = Array.from(document.querySelectorAll('input, select'));
              const idx = inputs.indexOf(e.target);
              if (idx >= 0 && inputs[idx + 1]) inputs[idx + 1].focus();
            }
          }}
          step="0.01"
          style={{
            width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, fontSize: 14,
            border: `1.5px solid ${C.borda}`, boxSizing: 'border-box',
            color: cor || C.texto, fontWeight: cor ? 700 : 400,
            outline: 'none', background: '#fafbfc',
          }}
        />
      </div>
    </div>
  );
}


function LinhaResumo({ label, valor, cor, destaque }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: destaque ? '10px 0' : '6px 0',
      borderTop: destaque ? `2px solid ${C.borda}` : 'none',
      marginTop: destaque ? 8 : 0,
    }}>
      <span style={{ fontSize: destaque ? 14 : 13, fontWeight: destaque ? 700 : 500, color: C.texto }}>
        {label}
      </span>
      <span style={{ fontSize: destaque ? 16 : 14, fontWeight: 700, color: cor || C.texto }}>
        {fmt(valor)}
      </span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Fechamento({ toast }) {
  const isMobile = useIsMobile();
  const [mes, setMes] = useState(mesAtual);
  const [ano, setAno] = useState(anoAtual);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('contratos');

  // ── Estado dos dados ──────────────────────────────────────────────────────
  const [contratos, setContratos] = useState([]);
  const [novoContrato, setNovoContrato] = useState({ nome: '', valor: '', data_inicio: '', data_fim: '' });
  const [editandoContrato, setEditandoContrato] = useState(null);
  const [showModalEditContrato, setShowModalEditContrato] = useState(false);

  const [reembolsos, setReembolsos] = useState([]);
  const [saldoDLAnterior, setSaldoDLAnterior] = useState(0);
  const [dlManual, setDlManual] = useState(''); // usado só quando não há mês anterior
  const [semFechamentoAnterior, setSemFechamentoAnterior] = useState(false);

  // Reservado — itens com CRUD (igual despesas)
  const [reservadoItens, setReservadoItens] = useState([]);
  const [reservadoValores, setReservadoValores] = useState({});
  const [novoReservado, setNovoReservado] = useState('');
  const [editandoReservado, setEditandoReservado] = useState(null);
  const [poupancaPerc, setPoupancaPerc] = useState(20);

  // Avulsos
  const [avulsos, setAvulsos] = useState({
    totalConta: '',
    saldoAnterior: '',
    entradaInter: '',
    entradaAsaas: '',
    entradaBomControle: '',
    boletosAgendados: '',
    outrasEntradas: ['', '', ''],
  });

  // Despesas dinâmicas (do banco)
  const [despesasItens, setDespesasItens] = useState([]); // lista de itens cadastrados
  const [despesasValores, setDespesasValores] = useState({}); // { [id]: valor } — só os valores do mês
  const [novaDespesa, setNovaDespesa] = useState({ nome: '', tipo: 'fixa', data_inicio: '', data_fim: '' });
  const [editandoDespesa, setEditandoDespesa] = useState(null);

  
  // Salários — dinâmicos por tenant
  const [salarios, setSalarios] = useState([]);
  const [salariosValores, setSalariosValores] = useState({});

  // 13° / Férias (quando houver)
 const [extras, setExtras] = useState({});

  // Reembolsos manuais (backup se API falhar)
  const [reembolsoManual, setReembolsoManual] = useState({});
  const [usuariosTenant, setUsuariosTenant] = useState([]);

// Sócios e bancos — dinâmicos por tenant
  const [socios, setSocios] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [bancosValores, setBancosValores] = useState({});

  // ── Cálculos ──────────────────────────────────────────────────────────────
  const totalContratos = contratos.reduce((s, c) => s + valorEfetivoContrato(c, mes, ano), 0);

  const totalContas = [
    ...bancos.map(b => bancosValores[b.id] || 0),
    ...avulsos.outrasEntradas
  ].reduce((s, v) => s + Number(v || 0), 0);

  const totalContasLiquido = totalContas - saldoDLAnterior;

  const totalSalarios = Object.values(salariosValores).reduce((s, v) => s + Number(v || 0), 0);

  const totalExtras = Object.values(extras).reduce((s, v) => s + Number(v || 0), 0);

  const totalReembolsosAPI = reembolsos.reduce((s, r) => s + Number(r.total_reembolso || 0), 0);
  const totalReembolsos = totalReembolsosAPI;

  const totalDespesasFixas = despesasItens
    .filter(d => d.tipo === 'fixa')
    .reduce((s, d) => s + Number(despesasValores[d.id] || 0), 0);

  const totalDespesasVariaveis = despesasItens
    .filter(d => d.tipo === 'variavel')
    .reduce((s, d) => s + Number(despesasValores[d.id] || 0), 0);

  const totalDespesas = totalDespesasFixas + totalDespesasVariaveis + totalSalarios + totalExtras + totalReembolsos;

  // Avulsos = Total Contas − Saldo D.L. Anterior − Total Contratos
const totalAvulsos = totalContas - saldoDLAnterior - totalContratos;
  // Lucro do Mês = Total Avulsos + Total Contratos - Total Despesas
  const lucroMes = totalAvulsos + totalContratos - totalDespesas;
  const dl = lucroMes * 0.2;
  const totalReservado = reservadoItens.reduce((s, r) => s + Number(reservadoValores[r.id] || 0), 0);

  // Saldo D.L. Atual = Lucro do Mês + Saldo D.L. Anterior
  const saldoDLAtual = lucroMes + saldoDLAnterior;


  // Divisão D.L. — dinâmica por tenant
  const poupancaDL = saldoDLAtual * (poupancaPerc / 100);
  const restanteDL = saldoDLAtual - poupancaDL;
  const divisaoSocios = socios.map(s => ({
    nome: s.nome,
    percentual: Number(s.percentual),
    valor: restanteDL * (Number(s.percentual) / 100)
  }));

  // Buscar Sócios
const buscarSocios = useCallback(async () => {
    try {
      const res = await api.get('/fechamento/socios');
      setSocios(res.data);
    } catch {}
  }, []);

   // Buscar Bancos — filtra por vigência (data_inicio/data_fim) do mês selecionado
  const buscarBancos = useCallback(async () => {
    try {
      const res = await api.get(`/fechamento/bancos?mes=${mes}&ano=${ano}`);
      setBancos(res.data);
    } catch {}
  }, [mes, ano]);

  // Buscar Salários
  // Filtra por vigência (data_inicio/data_fim) do mês selecionado — mesmo padrão
  // de contratos/despesas/bancos.
  const buscarSalarios = useCallback(async () => {
    try {
      const res = await api.get(`/salarios?mes=${mes}&ano=${ano}`);
      setSalarios(res.data);
      const valores = {};
      const extrasIniciais = {};
      res.data.forEach(s => {
        valores[`${s.id}_base`]      = s.salario_base  || 0;
        valores[`${s.id}_prolabore`] = s.pro_labore    || 0;
        valores[`${s.id}_quota`]     = s.quota         || 0;
        if (Number(s.decimo_terceiro) > 0)
          extrasIniciais[`dec13_${s.id}`] = s.decimo_terceiro;
        if (Number(s.ferias) > 0)
          extrasIniciais[`ferias_${s.id}`] = s.ferias;
      });
      setSalariosValores(valores);
      setExtras(prev => ({ ...extrasIniciais, ...prev }));
    } catch {}
  }, [mes, ano]);

  // ── Buscar dados ──────────────────────────────────────────────────────────
  const buscarContratos = useCallback(async () => {
    try {
      const res = await api.get(`/fechamento/contratos?mes=${mes}&ano=${ano}`);
      setContratos(res.data);
    } catch { }
  }, [mes, ano]);

  // Filtra por vigência (data_inicio/data_fim) do mês selecionado — evita que
  // uma despesa nova/encerrada apareça em meses onde não deveria existir.
  const buscarDespesas = useCallback(async () => {
    try {
      const res = await api.get(`/fechamento/despesas?mes=${mes}&ano=${ano}`);
      setDespesasItens(res.data);
    } catch { }
  }, [mes, ano]);

  const buscarReservado = useCallback(async () => {
    try {
      const res = await api.get('/fechamento/reservado');
      setReservadoItens(res.data);
    } catch { }
  }, []);

  const buscarUsuarios = useCallback(async () => {
    try {
      const res = await api.get('/usuarios');
      setUsuariosTenant(res.data);
    } catch {}
  }, []);

 const buscarReembolsos = useCallback(async () => {
  try {
    const [lanc, usrs] = await Promise.all([
      api.get('/lancamentos'),
      api.get('/usuarios'),
    ]);

    const mesPad = String(mes).padStart(2, '0');
    const anoStr = String(ano);

    const funcionarios = usrs.data;
    

    const resumo = funcionarios.map(u => {
      const lancFunc = lanc.data.filter(l => {
        // Pega apenas os primeiros 10 chars (YYYY-MM-DD) para evitar problema de timezone
        const dataStr = l.data ? l.data.substring(0, 10) : '';
        const [lAno, lMes] = dataStr.split('-');
        return l.usuario_id === u.id && lMes === mesPad && lAno === anoStr;
      });
      const total_reembolso = lancFunc.reduce((s, l) => s + parseFloat(l.valor || 0), 0);
      return { ...u, total_reembolso, lancamentos: lancFunc };
    }).filter(u => u.lancamentos.length > 0);

    setReembolsos(resumo);
  } catch {}
}, [mes, ano]);
  const buscarFechamento = useCallback(async () => {
    setCarregando(true);
    try {
      const ePrimeiroMes = mes === 1 && ano === 2026;
      const mesAnterior = mes === 1 ? 12 : mes - 1;
      const anoAnterior = mes === 1 ? ano - 1 : ano;

      // Busca DL do mês anterior (só se não for Jan/2026)
      if (!ePrimeiroMes) {
        try {
          const resAnterior = await api.get(`/fechamento?mes=${mesAnterior}&ano=${anoAnterior}`);
          if (resAnterior.data?.dados) {
            const dadosAnt = resAnterior.data.dados;
// Recalcula o lucro do mês anterior para pegar o valor correto
const dlAnterior = dadosAnt.saldoDLAtual !== undefined ? dadosAnt.saldoDLAtual : (dadosAnt.lucroMes || 0);
setSaldoDLAnterior(Number(dlAnterior));
            setSemFechamentoAnterior(false);
          } else {
            setSaldoDLAnterior(0);
            setSemFechamentoAnterior(false);
          }
        } catch { setSaldoDLAnterior(0); setSemFechamentoAnterior(false); }
      } else {
        // Janeiro/2026 = ponto de partida, sem mês anterior
        setSaldoDLAnterior(0);
        setSemFechamentoAnterior(false);
      }

      const res = await api.get(`/fechamento?mes=${mes}&ano=${ano}`);
      if (res.data?.dados) {
        const d = res.data.dados;
        if (d.bancosValores) setBancosValores(d.bancosValores);
        if (d.salariosValores) setSalariosValores(d.salariosValores);
        if (d.despesasValores) setDespesasValores(d.despesasValores);
        if (d.reservadoValores) setReservadoValores(d.reservadoValores);
        if (d.extras) setExtras(prev => ({ ...prev, ...d.extras }));
        if (d.reembolsoManual) setReembolsoManual(prev => ({ ...prev, ...d.reembolsoManual }));
        if (d.poupancaPerc !== undefined) setPoupancaPerc(Number(d.poupancaPerc));
        if (d.saldoDLInicial !== undefined) setSaldoDLAnterior(Number(d.saldoDLInicial || 0));
        // Restaura avulsos (inclui outrasEntradas) — sem isso os campos não preenchem ao recarregar
        if (d.avulsos) setAvulsos(prev => ({
          ...prev,
          ...d.avulsos,
          outrasEntradas: Array.isArray(d.avulsos.outrasEntradas)
            ? d.avulsos.outrasEntradas
            : ['', '', ''],
        }));
      } else {
        // Mês novo: copia despesas fixas do mês anterior
        if (!ePrimeiroMes) {
          try {
            const resAnterior = await api.get(`/fechamento?mes=${mesAnterior}&ano=${anoAnterior}`);
            if (resAnterior.data?.dados?.despesasValores) {
              const valoresAnteriores = resAnterior.data.dados.despesasValores;
              const resItens = await api.get(`/fechamento/despesas?mes=${mes}&ano=${ano}`);
              const fixas = resItens.data.filter(d => d.tipo === 'fixa').map(d => d.id);
              const novosValores = {};
              fixas.forEach(id => { if (valoresAnteriores[id]) novosValores[id] = valoresAnteriores[id]; });
              setDespesasValores(novosValores);
            } else { setDespesasValores({}); }
          } catch { setDespesasValores({}); }
        } else {
          setDespesasValores({});
        }
        setReservadoValores({});
        setAvulsos({ entradaInter: '', entradaAsaas: '', entradaBomControle: '', outrasEntradas: ['', '', ''] });
              }
    } catch { } finally {
      setCarregando(false);
    }
  }, [mes, ano]);

  useEffect(() => {
    buscarContratos();
    buscarDespesas();
    buscarReservado();
    buscarSocios();
    buscarBancos();
    buscarSalarios();
    buscarUsuarios();
  }, [buscarContratos, buscarDespesas, buscarReservado, buscarSocios, buscarBancos, buscarSalarios, buscarUsuarios]);

  useEffect(() => {
    buscarFechamento();
    buscarReembolsos();
  }, [buscarFechamento, buscarReembolsos]);

  // ── Salvar ────────────────────────────────────────────────────────────────
  const salvar = async () => {
    setSalvando(true);
    try {
      await api.post('/fechamento', {
        mes, ano,
      dados: { avulsos, bancosValores, salariosValores, despesasValores, reservadoValores, extras, reembolsoManual, dl, lucroMes, saldoDLAtual, poupancaPerc, ...(mes === 1 && ano === 2026 ? { saldoDLInicial: saldoDLAnterior } : {}) }
      });
      toast('Fechamento salvo com sucesso!', 'success');
    } catch {
      toast('Erro ao salvar fechamento.', 'error');
    } finally {
      setSalvando(false);
    }
  };

  // ── Exportar PDF ──────────────────────────────────────────────────────────
  const exportarPDF = () => {
    const conteudo = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fechamento ${MESES[mes-1]} ${ano}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; color: #1a2233; margin: 0; padding: 20px; }
          h1 { color: #D85A30; font-size: 20px; border-bottom: 2px solid #D85A30; padding-bottom: 8px; }
          h2 { color: #D85A30; font-size: 14px; margin-top: 20px; margin-bottom: 8px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
          th { background: #D85A30; color: white; padding: 6px 10px; text-align: left; font-size: 11px; }
          td { padding: 5px 10px; border-bottom: 1px solid #eee; }
          tr:nth-child(even) td { background: #fafafa; }
          .total { font-weight: bold; background: #fff3e0 !important; }
          .destaque { font-size: 14px; font-weight: bold; }
          .verde { color: #1db87a; }
          .vermelho { color: #e74c3c; }
          .resumo-box { background: #f7f8fa; border: 1px solid #e4e9f0; border-radius: 8px; padding: 16px; margin-top: 16px; }
          .resumo-linha { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #eee; }
        </style>
      </head>
      <body>
        <h1>Fechamento Mensal — ${MESES[mes-1]} ${ano}</h1>

        <h2>📋 Contratos</h2>
        <table>
          <tr><th>Empresa</th><th>Valor</th></tr>
          ${contratos.map(c => `<tr><td>${c.nome}</td><td>R$ ${valorEfetivoContrato(c, mes, ano).toFixed(2)}</td></tr>`).join('')}
          <tr class="total"><td>Total Contratos</td><td>R$ ${totalContratos.toFixed(2)}</td></tr>
        </table>

        <h2>💰 Entradas (Contas)</h2>
        <table>
          <tr><th>Origem</th><th>Valor</th></tr>
          ${bancos.map(b => `<tr><td>${b.nome}</td><td>R$ ${Number(bancosValores[b.id]||0).toFixed(2)}</td></tr>`).join('')}
          ${avulsos.outrasEntradas.map((v,i) => v ? `<tr><td>Entrada ${i+1}</td><td>R$ ${Number(v).toFixed(2)}</td></tr>` : '').join('')}
          <tr class="total"><td>Total Contas</td><td>R$ ${totalContas.toFixed(2)}</td></tr>
        </table>

        <h2>👥 Salários e Comissões</h2>
        <table>
          <tr><th>Funcionário</th><th>Valor</th></tr>
          ${salarios.length === 0
            ? '<tr><td colspan="2">Nenhum funcionário cadastrado</td></tr>'
            : salarios.map(s => {
                const valor = Number(salariosValores[`${s.id}_base`] || 0)
                  + Number(salariosValores[`${s.id}_prolabore`] || 0)
                  + Number(salariosValores[`${s.id}_quota`] || 0);
                return `<tr><td>${s.nome}</td><td>R$ ${valor.toFixed(2)}</td></tr>`;
              }).join('')}
          <tr class="total"><td>Total</td><td>R$ ${totalSalarios.toFixed(2)}</td></tr>
        </table>

        <h2>🧾 Despesas</h2>
        <table>
          <tr><th>Item</th><th>Tipo</th><th>Valor</th></tr>
          ${despesasItens.map(d => `<tr><td>${d.nome}</td><td>${d.tipo === 'fixa' ? 'Fixa' : 'Variável'}</td><td>R$ ${Number(despesasValores[d.id]||0).toFixed(2)}</td></tr>`).join('')}
          <tr class="total"><td colspan="2">Total Despesas</td><td>R$ ${totalDespesas.toFixed(2)}</td></tr>
        </table>

        <div class="resumo-box">
          <h2 style="margin-top:0">📊 Resumo Final</h2>
          <div class="resumo-linha"><span>Total Contratos</span><span>R$ ${totalContratos.toFixed(2)}</span></div>
          <div class="resumo-linha"><span>Total Contas</span><span>R$ ${totalContas.toFixed(2)}</span></div>
          <div class="resumo-linha"><span>Serviços Avulsos (Contas - Contratos)</span><span>R$ ${totalAvulsos.toFixed(2)}</span></div>
          <div class="resumo-linha"><span>Total Despesas</span><span class="vermelho">R$ ${totalDespesas.toFixed(2)}</span></div>
          <div class="resumo-linha destaque"><span>Lucro do Mês</span><span class="${lucroMes >= 0 ? 'verde' : 'vermelho'}">R$ ${lucroMes.toFixed(2)}</span></div>
          <div class="resumo-linha"><span>D.L. (20%)</span><span>R$ ${dl.toFixed(2)}</span></div>
          <div class="resumo-linha"><span>Reservado</span><span>R$ ${totalReservado.toFixed(2)}</span></div>
          ${saldoDLAnterior > 0 ? `<div class="resumo-linha"><span>Saldo D.L. Anterior (informativo)</span><span>R$ ${saldoDLAnterior.toFixed(2)}</span></div>` : ''}
        </div>
      </body>
      </html>
    `;
    const janela = window.open('', '_blank');
    janela.document.write(conteudo);
    janela.document.close();
    janela.focus();
    setTimeout(() => { janela.print(); janela.close(); }, 500);
  };

  // ── Exportar Excel (CSV) ──────────────────────────────────────────────────
  const exportarExcel = () => {
    const linhas = [
      ['FECHAMENTO MENSAL', `${MESES[mes-1]} ${ano}`],
      [],
      ['CONTRATOS'],
      ['Empresa', 'Valor'],
      ...contratos.map(c => [c.nome, valorEfetivoContrato(c, mes, ano)]),
      ['Total Contratos', totalContratos],
      [],
      ['ENTRADAS (CONTAS)'],
      ['Origem', 'Valor'],
      ...bancos.map(b => [b.nome, bancosValores[b.id] || 0]),
      ['Total Contas', totalContas],
      [],
      ['SALÁRIOS'],
      ['Funcionário', 'Valor'],
      ...salarios.map(s => [
        s.nome,
        Number(salariosValores[`${s.id}_base`] || 0)
          + Number(salariosValores[`${s.id}_prolabore`] || 0)
          + Number(salariosValores[`${s.id}_quota`] || 0)
      ]),
      ['Total Salários', totalSalarios],
      [],
      ['DESPESAS'],
      ['Item', 'Tipo', 'Valor'],
      ...despesasItens.map(d => [d.nome, d.tipo === 'fixa' ? 'Fixa' : 'Variável', despesasValores[d.id] || 0]),
      ['Total Despesas', '', totalDespesas],
      [],
      ['RESUMO FINAL'],
      ['Total Contratos', totalContratos],
      ['Total Contas', totalContas],
      ['Serviços Avulsos', totalAvulsos],
      ['Total Despesas', totalDespesas],
      ['Lucro do Mês', lucroMes],
      ['D.L. (20%)', dl],
      ['Reservado', totalReservado],
      ['Saldo D.L. Anterior', saldoDLAnterior],
    ];

    const csv = linhas.map(l => l.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Fechamento_${ano}-${mes}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast('Excel exportado!', 'success');
  };

  // ── Contratos: adicionar / editar ─────────────────────────────────────────
  const adicionarContrato = async () => {
    if (!novoContrato.nome || !novoContrato.valor) return;
    try {
      await api.post('/fechamento/contratos', novoContrato);
      setNovoContrato({ nome: '', valor: '', data_inicio: '', data_fim: '' });
      buscarContratos();
      toast('Contrato adicionado!', 'success');
    } catch { toast('Erro ao adicionar contrato.', 'error'); }
  };

  const salvarEdicaoContrato = async () => {
    try {
      await api.patch(`/fechamento/contratos/${editandoContrato.id}`, editandoContrato);
      setEditandoContrato(null);
      setShowModalEditContrato(false);
      buscarContratos();
      toast('Contrato atualizado!', 'success');
    } catch { toast('Erro ao atualizar contrato.', 'error'); }
  };

  const abrirEdicaoContrato = (c) => {
    setEditandoContrato({
      id: c.id,
      nome: c.nome,
      valor: c.valor,
      data_inicio: c.data_inicio ? c.data_inicio.substring(0, 10) : '',
      data_fim: c.data_fim ? c.data_fim.substring(0, 10) : '',
      data_ultimo_reajuste: c.data_ultimo_reajuste ? c.data_ultimo_reajuste.substring(0, 10) : '',
      valor_ultimo_reajuste: c.valor_ultimo_reajuste || '',
    });
    setShowModalEditContrato(true);
  };

  const removerContrato = async (id) => {
    try {
      await api.patch(`/fechamento/contratos/${id}`, { ativo: false });
      buscarContratos();
      toast('Contrato removido!', 'success');
    } catch { toast('Erro ao remover contrato.', 'error'); }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const abas = [
    { key: 'contratos', label: '📋 Contratos' },
    { key: 'avulsos', label: '💰 Contas' },
    { key: 'salarios', label: '👥 Salários' },
    { key: 'despesas', label: '🧾 Despesas' },
    { key: 'reembolsos', label: '🔄 Reembolsos' },
    { key: 'resumo', label: '📊 Resumo' },
  ];

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 20px', maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 800, color: C.texto }}>
            Fechamento Mensal
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textoSub }}>
            Controle financeiro mensal da Sobraa
          </p>
        </div>

        {/* Seletor mês/ano */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
          <select value={mes} onChange={e => setMes(Number(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, color: C.texto, background: C.card, flex: isMobile ? 1 : 'none' }}>
            {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <input type="number" value={ano} onChange={e => setAno(Math.max(2026, Number(e.target.value)))}
            min={2026}
            style={{ width: isMobile ? 80 : 80, padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, color: C.texto, background: C.card }} />

          <div style={{ display: 'flex', gap: 8, width: isMobile ? '100%' : 'auto' }}>
            <button onClick={salvar} disabled={salvando} style={{
              flex: 1, padding: '8px 14px', background: C.laranja, color: 'white',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer'
            }}>
              {salvando ? 'Salvando...' : '💾 Salvar'}
            </button>
            <button onClick={exportarPDF} style={{
              flex: 1, padding: '8px 14px', background: C.vermelho, color: 'white',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer'
            }}>📄 PDF</button>
            <button onClick={exportarExcel} style={{
              flex: 1, padding: '8px 14px', background: C.verde, color: 'white',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer'
            }}>📊 Excel</button>
          </div>
        </div>
      </div>

      {/* ── Cards de resumo rápido ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Contratos', valor: totalContratos, cor: C.azul },
          { label: 'Total Contas', valor: totalContas, cor: C.laranja },
          { label: 'Serviços Avulsos', valor: totalAvulsos, cor: C.verde },
          { label: 'Total Despesas', valor: totalDespesas, cor: C.vermelho },
          { label: 'Lucro do Mês', valor: lucroMes, cor: lucroMes >= 0 ? C.verde : C.vermelho },
        ].map(item => (
          <Card key={item.label} style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', letterSpacing: 0.8 }}>{item.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: item.cor, marginTop: 4 }}>{fmt(item.valor)}</div>
          </Card>
        ))}
      </div>

      {/* ── Abas ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {abas.map(a => (
          <button key={a.key} onClick={() => setAbaAtiva(a.key)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            fontWeight: 600, fontSize: 13,
            background: abaAtiva === a.key ? C.laranja : C.card,
            color: abaAtiva === a.key ? 'white' : C.textoSub,
            boxShadow: abaAtiva === a.key ? '0 2px 8px rgba(232,114,12,0.3)' : 'none',
            border: `1px solid ${abaAtiva === a.key ? C.laranja : C.borda}`,
          }}>
            {a.label}
          </button>
        ))}
      </div>

      {carregando && <div style={{ textAlign: 'center', color: C.textoSub, padding: 40 }}>Carregando...</div>}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: CONTRATOS */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'contratos' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20, alignItems: 'start' }}>
          <Card style={{ padding: isMobile ? '16px 10px' : 24 }}>
            <SecaoTitulo icone="📋" label={`Contratos Ativos — ${fmt(totalContratos)}`} />
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: C.textoSub, textTransform: 'uppercase', borderBottom: `2px solid ${C.borda}`, whiteSpace: 'nowrap' }}>Empresa</th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: C.textoSub, textTransform: 'uppercase', borderBottom: `2px solid ${C.borda}`, whiteSpace: 'nowrap' }}>Valor Mensal</th>
                  {!isMobile && <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, fontWeight: 700, color: C.textoSub, textTransform: 'uppercase', borderBottom: `2px solid ${C.borda}`, whiteSpace: 'nowrap' }}>Datas</th>}
                  <th style={{ borderBottom: `2px solid ${C.borda}` }}></th>
                </tr>
              </thead>
              <tbody>
                {contratos.map(c => (
                  <tr key={c.id} style={{ borderBottom: `1px solid ${C.borda}` }}>
                    <td style={{ padding: '9px 6px', fontSize: 13 }}>{c.nome}</td>
                    <td style={{ padding: '9px 6px', fontSize: 13, fontWeight: 600, color: C.azul, whiteSpace: 'nowrap' }}>
                      {fmt(valorEfetivoContrato(c, mes, ano))}
                      {(() => {
                        if (!c.data_ultimo_reajuste || !c.valor_ultimo_reajuste) return null;
                        const dr = new Date(c.data_ultimo_reajuste);
                        const antes = ano < dr.getFullYear() || (ano === dr.getFullYear() && mes < dr.getMonth() + 1);
                        if (!antes) return null;
                        return (
                          <span style={{ display: 'block', fontSize: 10, color: C.amarelo, fontWeight: 600 }}>
                            ⚠️ Reajuste em {dr.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })}
                          </span>
                        );
                      })()}
                    </td>
                    {!isMobile && (
                      <td style={{ padding: '9px 6px' }}>
                        {c.data_inicio && (
                          <span style={{ fontSize: 11, color: C.textoSub, display: 'block', whiteSpace: 'nowrap' }}>
                            Início: {new Date(c.data_inicio).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {c.data_fim && (
                          <span style={{ fontSize: 11, color: C.vermelho, display: 'block', whiteSpace: 'nowrap', fontWeight: 600 }}>
                            Encerrado: {new Date(c.data_fim).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {c.data_ultimo_reajuste && (() => {
                          const dr = new Date(c.data_ultimo_reajuste);
                          const jaReajustado = ano > dr.getFullYear() || (ano === dr.getFullYear() && mes >= dr.getMonth() + 1);
                          if (!jaReajustado) return null;
                          return (
                            <span style={{ fontSize: 11, color: C.textoSub, display: 'block', whiteSpace: 'nowrap' }}>
                              Último reajuste: {dr.toLocaleDateString('pt-BR')}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td style={{ padding: '9px 6px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button onClick={() => abrirEdicaoContrato(c)}
                          style={{ padding: '4px 8px', background: '#eef2ff', color: C.azul, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✏️</button>
                        <button onClick={() => removerContrato(c.id)}
                          style={{ padding: '4px 8px', background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          {/* Adicionar contrato */}
          <Card>
            <SecaoTitulo icone="➕" label="Novo Contrato" cor={C.verde} />
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Empresa</label>
              <input value={novoContrato.nome} onChange={e => setNovoContrato(p => ({ ...p, nome: e.target.value }))}
                placeholder="Nome da empresa"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Valor Mensal (R$)</label>
              <input type="number" value={novoContrato.valor} onChange={e => setNovoContrato(p => ({ ...p, valor: e.target.value }))}
                placeholder="0,00" step="0.01"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Início do Contrato</label>
              <input type="date" value={novoContrato.data_inicio} onChange={e => setNovoContrato(p => ({ ...p, data_inicio: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: C.textoSub, marginTop: 3 }}>
                Define a partir de qual mês este contrato aparece no fechamento.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Encerramento do Contrato</label>
              <input type="date" value={novoContrato.data_fim} onChange={e => setNovoContrato(p => ({ ...p, data_fim: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: C.textoSub, marginTop: 3 }}>
                Opcional. Deixe em branco para contrato ativo. O contrato deixará de aparecer nos meses posteriores a esta data.
              </div>
            </div>
            <button onClick={adicionarContrato} style={{
              width: '100%', padding: '10px', background: C.laranja, color: 'white',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer'
            }}>Adicionar Contrato</button>

            <div style={{ marginTop: 20, padding: '14px 16px', background: '#fff8f0', borderRadius: 10, border: `1px solid ${C.laranja}30` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.laranja, marginBottom: 8 }}>RESUMO</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: C.textoSub }}>{contratos.length} contratos</span>
                <span style={{ fontWeight: 800, color: C.laranja }}>{fmt(totalContratos)}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: AVULSOS */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'avulsos' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
          <Card>
            <SecaoTitulo icone="📥" label="Entradas até dia 20" cor={C.verde} />
           {bancos.length === 0 && (
  <p style={{ color: C.textoSub, fontSize: 13 }}>
    Nenhuma conta cadastrada. Adicione em Configurações → Fechamento.
  </p>
)}
{bancos.map(b => (
  <CampoValor
    key={b.id}
    label={b.nome}
    value={bancosValores[b.id] || ''}
    onChange={v => setBancosValores(p => ({ ...p, [b.id]: v }))}
    cor={C.verde}
  />
))}
            {avulsos.outrasEntradas.map((v, i) => (
              <CampoValor key={i} label={`Outra entrada ${i+1}`} value={v}
                onChange={val => setAvulsos(p => ({ ...p, outrasEntradas: p.outrasEntradas.map((x, j) => j === i ? val : x) }))} />
            ))}

            {/* Saldo DL do mês anterior */}
            {mes === 1 && ano === 2026 ? (
              <div style={{ marginTop: 12, padding: '12px 14px', background: '#fffbeb', borderRadius: 8, border: `1px solid ${C.amarelo}50` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.amarelo, marginBottom: 6 }}>
                  SALDO D.L. MÊS ANTERIOR — Ponto de partida (Janeiro/2026)
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: C.amarelo, fontWeight: 600, pointerEvents: 'none' }}>R$</span>
                  <input
                    type="number" step="0.01"
                    value={saldoDLAnterior || ''}
                    onChange={e => setSaldoDLAnterior(Number(e.target.value || 0))}
                    placeholder="0,00"
                    style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, fontSize: 14, border: `1.5px solid ${C.amarelo}`, boxSizing: 'border-box', background: '#fafbfc' }}
                  />
                </div>
                <div style={{ fontSize: 11, color: C.textoSub, marginTop: 4 }}>
                  Digite o saldo D.L. de dezembro/2025. A partir de fevereiro/2026 será automático.
                </div>
              </div>
            ) : saldoDLAnterior > 0 ? (
              <div style={{ marginTop: 12, padding: '12px 14px', background: '#eef2ff', borderRadius: 8, border: `1px solid ${C.azul}30` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.azul, marginBottom: 2 }}>
                  SALDO D.L. MÊS ANTERIOR (informativo)
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.azul }}>
                  {fmt(saldoDLAnterior)}
                </div>
                <div style={{ fontSize: 11, color: C.textoSub, marginTop: 2 }}>
                  Calculado automaticamente do fechamento anterior
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: 10, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: C.texto }}>Total Contas</span>
              <span style={{ fontWeight: 800, fontSize: 16, color: C.verde }}>{fmt(totalContas)}</span>
            </div>
          </Card>

          {/* Card Reservado */}
          <Card>
            <SecaoTitulo icone="🏦" label="Reservado" cor={C.azul} />
            {reservadoItens.length === 0 && (
              <p style={{ color: C.textoSub, fontSize: 13 }}>Nenhum item cadastrado.</p>
            )}
            {reservadoItens.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  {editandoReservado?.id === r.id ? (
                    <input value={editandoReservado.nome} onChange={e => setEditandoReservado(p => ({ ...p, nome: e.target.value }))}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1.5px solid ${C.azul}`, fontSize: 13, boxSizing: 'border-box', marginBottom: 4 }} />
                  ) : (
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 2 }}>{r.nome}</label>
                  )}
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: C.textoSub, fontWeight: 600, pointerEvents: 'none' }}>R$</span>
                    <input type="number" step="0.01"
                      value={reservadoValores[r.id] || ''}
                      onChange={e => setReservadoValores(p => ({ ...p, [r.id]: e.target.value }))}
                      style={{ width: '100%', padding: '7px 12px 7px 32px', borderRadius: 8, fontSize: 14, border: `1.5px solid ${C.borda}`, boxSizing: 'border-box', background: '#fafbfc' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {editandoReservado?.id === r.id ? (
                    <>
                      <button onClick={async () => { await api.patch(`/fechamento/reservado/${r.id}`, { nome: editandoReservado.nome }); setEditandoReservado(null); buscarReservado(); toast('Atualizado!', 'success'); }}
                        style={{ padding: '4px 8px', background: C.verde, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✓</button>
                      <button onClick={() => setEditandoReservado(null)}
                        style={{ padding: '4px 8px', background: C.borda, color: C.textoSub, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditandoReservado({ id: r.id, nome: r.nome })}
                        style={{ padding: '4px 8px', background: '#eef2ff', color: C.azul, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                      <button onClick={async () => { await api.patch(`/fechamento/reservado/${r.id}`, { ativo: false }); buscarReservado(); toast('Removido!', 'success'); }}
                        style={{ padding: '4px 8px', background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Adicionar novo item */}
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <input value={novoReservado} onChange={e => setNovoReservado(e.target.value)}
                placeholder="Ex: Poupança, Cripto..."
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 13, boxSizing: 'border-box' }} />
              <button onClick={async () => {
                if (!novoReservado) return;
                await api.post('/fechamento/reservado', { nome: novoReservado });
                setNovoReservado('');
                buscarReservado();
                toast('Item adicionado!', 'success');
              }} style={{ padding: '8px 14px', background: C.azul, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                + Add
              </button>
            </div>

            {reservadoItens.length > 0 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#eef2ff', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Total Reservado</span>
                <span style={{ fontWeight: 800, fontSize: 15, color: C.azul }}>
                  {fmt(reservadoItens.reduce((s, r) => s + Number(reservadoValores[r.id] || 0), 0))}
                </span>
              </div>
            )}

            {/* % Poupança D.L. */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.borda}` }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.textoSub, display: 'block', marginBottom: 6 }}>
                % POUPANÇA D.L.
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="number" min="0" max="100" step="1"
                  value={poupancaPerc}
                  onChange={e => setPoupancaPerc(Math.min(100, Math.max(0, Number(e.target.value))))}
                  style={{ width: 80, padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box', textAlign: 'center' }}
                />
                <span style={{ fontSize: 14, fontWeight: 600, color: C.textoSub }}>%</span>
                <span style={{ fontSize: 13, color: C.verde, fontWeight: 700 }}>
                  = {fmt(saldoDLAtual * (poupancaPerc / 100))}
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.textoSub, marginTop: 4 }}>
                Aplicado sobre o Saldo D.L. Atual no Resumo
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: SALÁRIOS */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'salarios' && (
  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
    <Card>
      <SecaoTitulo icone="👥" label="Salários / Quotas" />
      {salarios.length === 0 && (
        <p style={{ color: C.textoSub, fontSize: 13 }}>
          Nenhum salário cadastrado. Adicione em Financeiro → Salários.
        </p>
      )}
      {salarios.map(s => (
        <div key={s.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${C.borda}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.texto, marginBottom: 8 }}>{s.nome}</div>
          {Number(s.salario_base) > 0 && (
            <CampoValor
              label="Salário Base"
              value={salariosValores[`${s.id}_base`] || ''}
              onChange={v => setSalariosValores(p => ({ ...p, [`${s.id}_base`]: v }))}
            />
          )}
          {Number(s.pro_labore) > 0 && (
            <CampoValor
              label="Pró-labore"
              value={salariosValores[`${s.id}_prolabore`] || ''}
              onChange={v => setSalariosValores(p => ({ ...p, [`${s.id}_prolabore`]: v }))}
            />
          )}
          {Number(s.quota) > 0 && (
            <CampoValor
              label="Quota"
              value={salariosValores[`${s.id}_quota`] || ''}
              onChange={v => setSalariosValores(p => ({ ...p, [`${s.id}_quota`]: v }))}
            />
          )}
        </div>
      ))}
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff8f0', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: C.texto }}>Total Salários</span>
        <span style={{ fontWeight: 800, fontSize: 16, color: C.laranja }}>{fmt(totalSalarios)}</span>
      </div>
    </Card>

    <Card>
      <SecaoTitulo icone="🎁" label="13° Salário / Férias" cor={C.amarelo} />
      {salarios.length === 0 && (
        <p style={{ color: C.textoSub, fontSize: 13 }}>
          Cadastre funcionários em Financeiro → Salários.
        </p>
      )}
      {salarios.map(s => (
        <div key={s.id}>
          <CampoValor
            label={`13° ${s.nome}`}
            value={extras[`dec13_${s.id}`] || ''}
            onChange={v => setExtras(p => ({ ...p, [`dec13_${s.id}`]: v }))}
          />
          <CampoValor
            label={`Férias ${s.nome}`}
            value={extras[`ferias_${s.id}`] || ''}
            onChange={v => setExtras(p => ({ ...p, [`ferias_${s.id}`]: v }))}
          />
        </div>
      ))}
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: C.texto }}>Total Extras</span>
        <span style={{ fontWeight: 800, fontSize: 16, color: C.amarelo }}>{fmt(totalExtras)}</span>
      </div>
    </Card>
  </div>
)}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: DESPESAS */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'despesas' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: 20 }}>

          {/* Lista de despesas com valores */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Fixas */}
            <Card>
              <SecaoTitulo icone="🔒" label="Despesas Fixas" cor={C.vermelho} />
              {despesasItens.filter(d => d.tipo === 'fixa').length === 0 && (
                <p style={{ color: C.textoSub, fontSize: 13 }}>Nenhuma despesa fixa cadastrada.</p>
              )}
              {despesasItens.filter(d => d.tipo === 'fixa').map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    {editandoDespesa?.id === d.id ? (
                      <input value={editandoDespesa.nome} onChange={e => setEditandoDespesa(p => ({ ...p, nome: e.target.value }))}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1.5px solid ${C.laranja}`, fontSize: 13, boxSizing: 'border-box' }} />
                    ) : (
                      <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 2 }}>{d.nome}</label>
                    )}
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: C.textoSub, fontWeight: 600, pointerEvents: 'none' }}>R$</span>
                      <input
                        type="number" step="0.01"
                        value={despesasValores[d.id] || ''}
                        onChange={e => setDespesasValores(p => ({ ...p, [d.id]: e.target.value }))}
                        style={{ width: '100%', padding: '7px 12px 7px 32px', borderRadius: 8, fontSize: 14, border: `1.5px solid ${C.borda}`, boxSizing: 'border-box', background: '#fafbfc' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {editandoDespesa?.id === d.id ? (
                      <>
                        <button onClick={async () => { await api.patch(`/fechamento/despesas/${d.id}`, { nome: editandoDespesa.nome }); setEditandoDespesa(null); buscarDespesas(); toast('Atualizado!', 'success'); }}
                          style={{ padding: '4px 8px', background: C.verde, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✓</button>
                        <button onClick={() => setEditandoDespesa(null)}
                          style={{ padding: '4px 8px', background: C.borda, color: C.textoSub, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditandoDespesa({ id: d.id, nome: d.nome })}
                          style={{ padding: '4px 8px', background: '#eef2ff', color: C.azul, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                        <button
                          title="Encerra a despesa a partir deste mês, mantendo-a nos meses anteriores"
                          onClick={async () => {
                            const dataEncerramento = new Date(ano, mes - 1, 1).toISOString().substring(0, 10);
                            await api.patch(`/fechamento/despesas/${d.id}`, { data_fim: dataEncerramento });
                            buscarDespesas();
                            toast('Despesa encerrada a partir deste mês.', 'success');
                          }}
                          style={{ padding: '4px 8px', background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>⏹️</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 8, padding: '10px 14px', background: '#fef2f2', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Total Fixas</span>
                <span style={{ fontWeight: 800, fontSize: 15, color: C.vermelho }}>{fmt(totalDespesasFixas)}</span>
              </div>
            </Card>

            {/* Variáveis */}
            <Card>
              <SecaoTitulo icone="💳" label="Despesas Variáveis" cor={C.amarelo} />
              {despesasItens.filter(d => d.tipo === 'variavel').length === 0 && (
                <p style={{ color: C.textoSub, fontSize: 13 }}>Nenhuma despesa variável cadastrada.</p>
              )}
              {despesasItens.filter(d => d.tipo === 'variavel').map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    {editandoDespesa?.id === d.id ? (
                      <input value={editandoDespesa.nome} onChange={e => setEditandoDespesa(p => ({ ...p, nome: e.target.value }))}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1.5px solid ${C.laranja}`, fontSize: 13, boxSizing: 'border-box' }} />
                    ) : (
                      <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 2 }}>{d.nome}</label>
                    )}
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: C.textoSub, fontWeight: 600, pointerEvents: 'none' }}>R$</span>
                      <input
                        type="number" step="0.01"
                        value={despesasValores[d.id] || ''}
                        onChange={e => setDespesasValores(p => ({ ...p, [d.id]: e.target.value }))}
                        style={{ width: '100%', padding: '7px 12px 7px 32px', borderRadius: 8, fontSize: 14, border: `1.5px solid ${C.borda}`, boxSizing: 'border-box', background: '#fafbfc' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {editandoDespesa?.id === d.id ? (
                      <>
                        <button onClick={async () => { await api.patch(`/fechamento/despesas/${d.id}`, { nome: editandoDespesa.nome }); setEditandoDespesa(null); buscarDespesas(); toast('Atualizado!', 'success'); }}
                          style={{ padding: '4px 8px', background: C.verde, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>✓</button>
                        <button onClick={() => setEditandoDespesa(null)}
                          style={{ padding: '4px 8px', background: C.borda, color: C.textoSub, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditandoDespesa({ id: d.id, nome: d.nome })}
                          style={{ padding: '4px 8px', background: '#eef2ff', color: C.azul, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏️</button>
                        <button
                          title="Encerra a despesa a partir deste mês, mantendo-a nos meses anteriores"
                          onClick={async () => {
                            const dataEncerramento = new Date(ano, mes - 1, 1).toISOString().substring(0, 10);
                            await api.patch(`/fechamento/despesas/${d.id}`, { data_fim: dataEncerramento });
                            buscarDespesas();
                            toast('Despesa encerrada a partir deste mês.', 'success');
                          }}
                          style={{ padding: '4px 8px', background: '#fef2f2', color: C.vermelho, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>⏹️</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 8, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Total Variáveis</span>
                <span style={{ fontWeight: 800, fontSize: 15, color: C.amarelo }}>{fmt(totalDespesasVariaveis)}</span>
              </div>
            </Card>
          </div>

          {/* Painel direito: adicionar + resumo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card>
              <SecaoTitulo icone="➕" label="Nova Despesa" cor={C.verde} />
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Nome</label>
                <input value={novaDespesa.nome} onChange={e => setNovaDespesa(p => ({ ...p, nome: e.target.value }))}
                  placeholder="Ex: Contabilidade"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Tipo</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {['fixa', 'variavel'].map(t => (
                    <button key={t} onClick={() => setNovaDespesa(p => ({ ...p, tipo: t }))} style={{
                      padding: '8px', border: `2px solid ${novaDespesa.tipo === t ? (t === 'fixa' ? C.vermelho : C.amarelo) : C.borda}`,
                      borderRadius: 8, background: novaDespesa.tipo === t ? (t === 'fixa' ? '#fef2f2' : '#fffbeb') : 'white',
                      cursor: 'pointer', fontWeight: 600, fontSize: 13,
                      color: novaDespesa.tipo === t ? (t === 'fixa' ? C.vermelho : C.amarelo) : C.textoSub
                    }}>
                      {t === 'fixa' ? '🔒 Fixa' : '💳 Variável'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>Início (opcional)</label>
                <input type="date" value={novaDespesa.data_inicio} onChange={e => setNovaDespesa(p => ({ ...p, data_inicio: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: C.textoSub, marginTop: 3 }}>
                  Deixe em branco para aparecer em todos os meses. Preencha para valer só a partir deste mês.
                </div>
              </div>
              <button onClick={async () => {
                if (!novaDespesa.nome) return;
                await api.post('/fechamento/despesas', novaDespesa);
                setNovaDespesa({ nome: '', tipo: 'fixa', data_inicio: '', data_fim: '' });
                buscarDespesas();
                toast('Despesa adicionada!', 'success');
              }} style={{ width: '100%', padding: '10px', background: C.laranja, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Adicionar Despesa
              </button>
            </Card>

            <Card style={{ background: '#fef2f2' }}>
              <SecaoTitulo icone="📊" label="Resumo Despesas" cor={C.vermelho} />
              <LinhaResumo label="Despesas Fixas" valor={totalDespesasFixas} cor={C.vermelho} />
              <LinhaResumo label="Despesas Variáveis" valor={totalDespesasVariaveis} cor={C.amarelo} />
              <LinhaResumo label="Salários" valor={totalSalarios} />
              <LinhaResumo label="Reembolsos" valor={totalReembolsos} />
              <LinhaResumo label="Total Despesas" valor={totalDespesas} cor={C.vermelho} destaque />
            </Card>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: REEMBOLSOS */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'reembolsos' && (
  <div>
    <p style={{ color: C.textoSub, fontSize: 13, marginBottom: 16 }}>
      {reembolsos.length > 0
        ? `Reembolsos pagos importados automaticamente do sistema para ${MESES[mes-1]}/${ano}.`
        : `Nenhum reembolso registrado no sistema para ${MESES[mes-1]}/${ano}.`}
    </p>

    {/* Lista de funcionários — sempre no formato visual */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
      {(reembolsos.length > 0 ? reembolsos : usuariosTenant).map(r => {
        const total = reembolsos.length > 0
          ? Number(r.total_reembolso || 0)
          : 0;
        const iniciais = r.iniciais || (r.nome || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        return (
          <Card key={r.id || r.iniciais}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: C.laranja, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13
                }}>
                  {iniciais}
                </div>
                <span style={{ fontWeight: 700, fontSize: 15, color: C.texto }}>{r.nome}</span>
              </div>
              <span style={{ fontWeight: 800, fontSize: 16, color: total > 0 ? C.laranja : C.textoSub }}>
                {fmt(total)}
              </span>
            </div>
          </Card>
        );
      })}
    </div>

    {/* Total geral */}
    <div style={{ marginTop: 20, padding: '14px 20px', background: '#fff8f0', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontWeight: 700, fontSize: 15, color: C.texto }}>Total Reembolsos</span>
      <span style={{ fontWeight: 800, fontSize: 18, color: C.laranja }}>{fmt(totalReembolsosAPI)}</span>
    </div>
  </div>
)}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ABA: RESUMO */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {abaAtiva === 'resumo' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
          <Card>
            <SecaoTitulo icone="📊" label={`Fechamento — ${MESES[mes-1]} ${ano}`} />
            <LinhaResumo label="Total Contratos" valor={totalContratos} cor={C.azul} />
            <LinhaResumo label="Total Contas" valor={totalContas} cor={C.laranja} />
            <LinhaResumo label="Serviços Avulsos (Contas − Saldo D.L. Anterior − Contratos)" valor={totalAvulsos} cor={C.verde} destaque />
            <div style={{ marginTop: 16 }} />
            <LinhaResumo label="Salários / Quotas" valor={totalSalarios} />
            <LinhaResumo label="13° / Férias" valor={totalExtras} />
            <LinhaResumo label="Despesas Fixas" valor={totalDespesasFixas} />
            <LinhaResumo label="Despesas Variáveis" valor={totalDespesasVariaveis} />
            <LinhaResumo label="Reembolsos" valor={totalReembolsos} />
            <LinhaResumo label="Total Despesas" valor={totalDespesas} cor={C.vermelho} destaque />
            <div style={{ marginTop: 16 }} />
            <LinhaResumo label="Lucro do Mês" valor={lucroMes} cor={lucroMes >= 0 ? C.verde : C.vermelho} destaque />
            <LinhaResumo label="Reservado" valor={totalReservado} cor={C.verde} />

            {/* Saldo D.L. */}
            <div style={{ marginTop: 16, padding: '14px 16px', background: '#eef2ff', borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.azul, marginBottom: 10, textTransform: 'uppercase' }}>Saldo D.L.</div>
              <LinhaResumo label="Saldo D.L. Anterior" valor={saldoDLAnterior} cor={saldoDLAnterior >= 0 ? C.azul : C.vermelho} />
              <LinhaResumo label="Saldo D.L. Atual" valor={saldoDLAtual} cor={saldoDLAtual >= 0 ? C.azul : C.vermelho} destaque />
            </div>

            {/* Divisão D.L. */}
            <div style={{ marginTop: 12, padding: '14px 16px', background: '#f0fdf4', borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.verde, marginBottom: 10, textTransform: 'uppercase' }}>Divisão D.L. (sobre Saldo D.L. Atual)</div>
              <LinhaResumo label={`Poupança (${poupancaPerc}%)`} valor={poupancaDL} cor={C.verde} />
{divisaoSocios.length > 0 && (
  <>
    <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: C.textoSub, marginBottom: 6 }}>
      RESTANTE (80%) DIVIDIDO:
    </div>
    {divisaoSocios.map(s => (
      <LinhaResumo
        key={s.nome}
        label={`${s.nome} (${s.percentual}%)`}
        valor={s.valor}
      />
    ))}
  </>
)}
{divisaoSocios.length === 0 && (
  <p style={{ fontSize: 12, color: C.textoSub, marginTop: 8 }}>
    Cadastre os sócios em Configurações → Fechamento.
  </p>
)}
            </div>
          </Card>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Lucro do Mês', valor: lucroMes, cor: lucroMes >= 0 ? C.verde : C.vermelho, icone: lucroMes >= 0 ? '📈' : '📉' },
              { label: 'Reservado', valor: totalReservado, cor: C.verde, icone: '🏦' },
              { label: 'Saldo D.L. Anterior', valor: saldoDLAnterior, cor: saldoDLAnterior >= 0 ? C.azul : C.vermelho, icone: '💰' },
              { label: 'Saldo D.L. Atual', valor: saldoDLAtual, cor: saldoDLAtual >= 0 ? C.azul : C.vermelho, icone: '💼' },
            ].map(item => (
              <Card key={item.label} style={{ padding: '20px 24px' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icone}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, textTransform: 'uppercase', letterSpacing: 0.8 }}>{item.label}</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: item.cor, marginTop: 4 }}>{fmt(item.valor)}</div>
              </Card>
            ))}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={exportarPDF} style={{ flex: 1, padding: '14px', background: C.vermelho, color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                📄 Exportar PDF
              </button>
              <button onClick={exportarExcel} style={{ flex: 1, padding: '14px', background: C.verde, color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                📊 Exportar Excel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR CONTRATO */}
      {showModalEditContrato && editandoContrato && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setShowModalEditContrato(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: isMobile ? 20 : 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20, color: C.texto }}>✏️ Editar Contrato</div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>EMPRESA</label>
              <input value={editandoContrato.nome} onChange={e => setEditandoContrato(p => ({ ...p, nome: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>VALOR MENSAL (R$)</label>
              <input type="number" step="0.01" value={editandoContrato.valor} onChange={e => setEditandoContrato(p => ({ ...p, valor: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>DATA DE INÍCIO DO CONTRATO</label>
              <input type="date" value={editandoContrato.data_inicio} onChange={e => setEditandoContrato(p => ({ ...p, data_inicio: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>DATA DE ENCERRAMENTO DO CONTRATO</label>
              <input type="date" value={editandoContrato.data_fim} onChange={e => setEditandoContrato(p => ({ ...p, data_fim: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: C.textoSub, marginTop: 3 }}>
                Preencha para encerrar o contrato. O contrato deixará de aparecer nos meses posteriores a esta data, mas permanece no histórico.
              </div>
            </div>

            <div style={{ padding: '16px', background: '#f7f8fa', borderRadius: 10, marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.textoSub, marginBottom: 12 }}>ÚLTIMO REAJUSTE</div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>DATA DO REAJUSTE</label>
                <input type="date" value={editandoContrato.data_ultimo_reajuste} onChange={e => setEditandoContrato(p => ({ ...p, data_ultimo_reajuste: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textoSub, display: 'block', marginBottom: 4 }}>VALOR APÓS O REAJUSTE (R$)</label>
                <input type="number" step="0.01" value={editandoContrato.valor_ultimo_reajuste} onChange={e => setEditandoContrato(p => ({ ...p, valor_ultimo_reajuste: e.target.value }))}
                  placeholder="0,00"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.borda}`, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              {editandoContrato.valor_ultimo_reajuste && editandoContrato.valor && (() => {
                const valorAntigo = Number(editandoContrato.valor);
                const valorNovo   = Number(editandoContrato.valor_ultimo_reajuste);
                const diff = valorNovo - valorAntigo;
                const perc = valorAntigo > 0 ? ((diff / valorAntigo) * 100).toFixed(1) : '0.0';
                const cor  = diff >= 0 ? C.verde : C.vermelho;
                const label = diff >= 0 ? 'Aumento' : 'Redução';
                return (
                  <div style={{ marginTop: 10, fontSize: 13, color: cor, fontWeight: 600 }}>
                    {label}: {fmt(Math.abs(diff))} ({Math.abs(Number(perc))}%)
                    <div style={{ fontSize: 11, color: C.textoSub, fontWeight: 400, marginTop: 2 }}>
                      De {fmt(valorAntigo)} → para {fmt(valorNovo)}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModalEditContrato(false)}
                style={{ flex: 1, padding: '10px', background: '#f0f3f7', color: C.texto, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={salvarEdicaoContrato}
                style={{ flex: 1, padding: '10px', background: C.laranja, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}